import dayjs from 'dayjs';
import { makeObservable, observable, action, computed, runInAction } from 'mobx';
import { message } from 'antd';

import i18n from 'src/i18n';
import { QUEUE_SCHEDULE_TYPE, TIME_RANGE, SOURCE_CATEGORY_TYPE, SOURCE_CATEGORY_TC, TOPIC_ATTRIBUTE_TYPE, AI_COMMENT_STATUS, AI_COMMENT_STATUS_DESC } from 'src/constants/normal';

import ProjectService from 'src/services/project';
import EDMService from 'src/services/edm';
import ResultService from 'src/services/result';
import mainStore from 'src/stores/mainStore';

// > 2025/04/24 updated -> 要篩掉 news, forum, threads, ig 的重複 url  話題
// ? 因為這邊 paging 的方式是固定 頁數 * 每頁筆數, 所以：
// ? 如果第一次撈完去重之後，剩下的筆數是 37, 要再去撈下 50筆
// ? 等到後面 50 筆也處理完之後，假設現在有 83 筆，一樣只顯示 50 筆
// ? 但把多的 33 筆保留在 remainingTopicList 裡面。
// ? 撈下一頁的時候，不用考慮這些目前沒有用到的，繼續往後面撈，然後再處理。
// ? 直到下一次超過50筆時，再把 新的 50 推到 topicList 裡面。
// ? 剩下的一樣再推回 remainingTopicList 裡面。
// ! 就可以避免因為志維的 anchor 是沒有 id 的關係，導致在撈下一頁的時候，會撈到重複的話題。

export default class DirectClientMarketingResultPageViewModel {
  projectId = '';
  newsletterId = '';
  resultId = '';
  startDate = '';
  endDate = '';
  edmType = 'edm';

  @observable newsletterName = '電子報主題';
  @observable timeText = '';
  @observable tagPool = [];
  @observable subjectList = [];
  @observable selectedSubject = null;
  @observable mainBrand = {};
  @observable mainBrandWOMCount = 0;
  @observable mainBrandChannels = [];
  @observable mainBrandSentiments = [];
  @observable mainBrandFeatures = [];
  @observable manualStatistics = [];

  @observable searchAngle = 'brand';

  @observable isSummaryCalculate = false;

  // ? 2024/11/01 新增auto summary選項，所以需要狀態來判斷是否顯示重新產生自動摘要Btn
  @observable autoSummary = false;
  // ? 2025/03/30 新增ai comment選項，所以需要狀態來判斷是否顯示ai comment 的 btn
  @observable aiComment = false;
  @observable isMemoUpdating = false;
  @observable memo = '';
  memoCache = '';

  @observable isShowRefreshAutoSummaryModal = false;
  refreshAutoSummaryResolve = null;

  @observable topics = [];
  searchId = null;
  // > 新增剩下的 topics list 來保留超過 limit 的筆數, 下次再撈下一頁的時候, 可以繼續使用這些目前沒有在畫面上的。
  @observable remainingTopicList = [];
  @observable hasNextPage = true;

  // > resize related
  resizeObserver = null;
  @observable width = 1920;

  // > 用來過濾重複話題的
  @observable urlSet = new Set();

  @observable currentPage = 1;

  // ? 2025/03/30 新增ai comment 選項，需要顯示目前 ai comment 的分析狀態
  @observable aiCommentStatus = null;
  @observable aiCommentStatusDesc = '';

  @observable lastActionTime = null;
  @observable remainingTime = 0;

  @computed get ratingTags() {
    return this.tagPool.filter((tag) => tag.type === 'rating');
  }

  @computed get availableRatingTags() {
    const result = {};
    this.ratingTags.forEach((tag) => {
      if (!tag.deletedAt) {
        result[`${tag.value}`] = tag.label;
      }
    });
    return result;
  }

  @computed get ratingTagsObject() {
    const tagsMap = new Map(this.ratingTags.map((item) => [item.value, item.label]));
    return Object.fromEntries(tagsMap);
  }

  @computed get topicTags() {
    return this.tagPool.filter((tag) => tag.type === 'topic');
  }

  @computed get suggestionTags() {
    return this.tagPool.filter((tag) => tag.type === 'suggestion');
  }

  @computed get departmentTags() {
    return this.tagPool.filter((tag) => tag.type === 'department');
  }

  @computed get selectedSubjectContent() {
    return this.subjectList.find((subject) => subject.value === this.selectedSubject);
  }

  @computed get ratingTagsIndex() {
    return this.ratingTags.map((item) => item.value);
  }

  @computed get coolDownTime() {
    return this.lastActionTime ? dayjs.duration(performance.now() - this.lastActionTime).asSeconds() : 0;
  }

  constructor() {
    makeObservable(this);
  }

  @action didMount = async (params) => {
    this.registerObserver();
    const {
      id,
      rid
    } = params;

    if (this.newsletterId) {
      return;
    }

    this.newsletterId = id;
    this.resultId = rid;

    await this.getNewsletterDetail();

    await Promise.all([
      this.updateSearchAngle(),
      this.getResultDetail()
    ]);

    await Promise.all([
      this.getResultSummary(),
      this.processTopics(),
      this.getAiCommentStatus()
    ]);
  };

  @action registerObserver = () => {
    const resizeObserver = new ResizeObserver((entries) => {
      const { width } = entries[0].contentRect;
      runInAction(() => {
        this.width = width;
      });
    });
    this.resizeObserver = resizeObserver;
    const container = document.querySelector('#app');
    this.resizeObserver.observe(container);
  };

  @action willUnmount = () => {
    const container = document.querySelector('#app');
    this.resizeObserver.unobserve(container);
  };

  @action getNewsletterDetail = async () => {
    try {
      if (mainStore.checkIsInQueue('newsletterDetail')) {
        return;
      }
      mainStore.setLoading('newsletterDetail');

      const { projectId, name, tags, autoSummary, aiComment } = await EDMService.getEDMDetail(this.newsletterId);
      runInAction(() => {
        this.projectId = projectId;
        this.newsletterName = name;
        this.tagPool = tags.map((tag) => ({ value: tag.id, label: tag.value, type: tag.type, deletedAt: tag.deletedAt, className: tag.deletedAt ? 'hideOption' : 'option' }));
        this.autoSummary = autoSummary;
        this.aiComment = aiComment;
      });
    } catch (error) {
      message.error(i18n.t('api_get_edm_detail_error'));
      this.basicDataInit = false;
    } finally {
      mainStore.setLoadingComplete('newsletterDetail');
    }
  };

  @action getResultDetail = async () => {
    try {
      if (mainStore.checkIsInQueue('resultDetail')) {
        return;
      }
      mainStore.setLoading('resultDetail');

      const { startDate, endDate, type, memo, topics, mainBrandId } = await ResultService.getResultDetail(this.newsletterId, this.resultId);
      const subjectList = await Promise.all([
        ...topics.map((item) => this.updateSubjectContent(item))
      ]);

      runInAction(() => {
        this.startDate = startDate;
        this.endDate = endDate;
        this.timeText = [QUEUE_SCHEDULE_TYPE.Daily, QUEUE_SCHEDULE_TYPE.Evening, QUEUE_SCHEDULE_TYPE.Noon, QUEUE_SCHEDULE_TYPE.Night].includes(type)
          ? `${dayjs(startDate).tz('Asia/Taipei').format('YYYY/MM/DD')} ${TIME_RANGE[type]}`
          : `${dayjs(startDate).tz('Asia/Taipei').format('YYYY/MM/DD')} ${TIME_RANGE[type]} - ${dayjs(endDate).tz('Asia/Taipei').format('YYYY/MM/DD')} ${TIME_RANGE[type]}`;
        this.memo = memo ?? '';
        this.memoCache = memo ?? '';
        this.subjectList = subjectList;
        this.selectedSubject = this.subjectList[0].value;
        this.mainBrand.id = mainBrandId;
      });
      await this.updateMainBrand();
    } catch (error) {
      message.error(i18n.t('api_get_edm_result_detail_error'));
      this.basicDataInit = false;
    } finally {
      mainStore.setLoadingComplete('resultDetail');
    }
  };

  @action getResultSummary = async () => {
    if (this.isSummaryCalculate) {
      return;
    }
    this.isSummaryCalculate = true;
    try {
      const {
        channel,
        feature,
        sentiment,
        subject,
        womCount
      } = await ResultService.getResultSummary(this.newsletterId, { gte: this.startDate, lte: this.endDate });

      runInAction(() => {
        this.mainBrandChannels = channel.slice().sort((a, b) => b.count - a.count);
        this.mainBrandWOMCount = womCount;
        this.mainBrandSentiments = [
          {
            key: 'positive',
            name: i18n.t('common_positive'),
            count: sentiment?.positiveCount ?? 0
          },
          {
            key: 'negative',
            name: i18n.t('common_negative'),
            count: sentiment?.negativeCount ?? 0
          },
          {
            key: 'neutral',
            name: i18n.t('common_neutral'),
            count: sentiment?.neutralCount ?? 0
          }
        ];
        this.mainBrandFeatures = feature;
        this.manualStatistics = subject.filter((sub) => this.subjectList.find((item) => item.value === sub.id && item.hasEvaluation)).map((sub) => ({
          id: sub.id,
          name: this.subjectList.find((item) => item.value === sub.id).label,
          rating: sub.rating.filter((r) => this.availableRatingTags[r.id]).sort((a, b) => this.ratingTagsIndex.indexOf(a.id) - this.ratingTagsIndex.indexOf(b.id)).map((r) => ({
            id: r.id,
            name: this.availableRatingTags[r.id] ?? '無',
            count: r.count
          }))
        }));
      });

    } catch (error) {
      message.error(i18n.t('api_get_manual_summary_error'));
    } finally {
      runInAction(() => {
        this.isSummaryCalculate = false;
      });
    }
  };

  @action onRefreshSummary = async () => {
    if (this.isSummaryCalculate) {
      return;
    }
    this.isSummaryCalculate = true;
    try {

      const {
        subject
      } = await ResultService.getResultSummary(this.newsletterId, { gte: this.startDate, lte: this.endDate });

      runInAction(() => {
        this.manualStatistics = subject.filter((sub) => this.subjectList.find((item) => item.value === sub.id && item.hasEvaluation)).map((sub) => ({
          id: sub.id,
          name: this.subjectList.find((item) => item.value === sub.id).label,
          rating: sub.rating.filter((r) => this.availableRatingTags[r.id]).sort((a, b) => this.ratingTagsIndex.indexOf(a.id) - this.ratingTagsIndex.indexOf(b.id)).map((r) => ({
            id: r.id,
            name: this.availableRatingTags[r.id] ?? '無',
            count: r.count
          }))
        }));
      });

      message.success(i18n.t('result_edit_page_calculate_message_success'));

    } catch (error) {
      message.success(i18n.t('result_edit_page_calculate_message_error'));
    } finally {
      runInAction(() => {
        this.isSummaryCalculate = false;
      });
    }
  };

  // > 2024/10/17 added -> refresh auto summary
  @action onRefreshAutoSummary = async () => {
    try {
      if (this.memo.trim()) {
        const result = await this.showRefreshAutoConfirmModal();
        runInAction(() => {
          this.isShowRefreshAutoSummaryModal = false;
          this.refreshAutoSummaryResolve = null;
        });

        if (!result) {
          return;
        }
      }
      if (mainStore.checkIsInQueue('autoSummary')) {
        return;
      }
      mainStore.setLoading('autoSummary');
      const summary = await ResultService.refreshAutoSummary(this.newsletterId, this.resultId);
      console.log('summary', summary);
      if (!summary.trim()) {
        throw new Error('auto summary is empty');
      }
      runInAction(() => {
        this.memo = summary;
      });
      await this.onMemoEditEnd();
    } catch (error) {
      message.error(i18n.t('api_get_auto_summary_error'));
    } finally {
      mainStore.setLoadingComplete('autoSummary');
    }
  };

  @action showRefreshAutoConfirmModal = async () => {
    return new Promise((resolve) => {
      this.isShowRefreshAutoSummaryModal = true;
      this.refreshAutoSummaryResolve = resolve;
    });
  };

  // > 2025/04/24 updated -> 要篩掉 news, forum, threads, ig 的重複 url  話題
  @action processTopics = async () => {
    if (this.remainingTopicList.length > 20) {
      this.topics = [...this.topics, ...this.remainingTopicList.slice(0, 20)];
      this.remainingTopicList = this.remainingTopicList.slice(20);
      return;
    }

    if (this.hasNextPage) {
      try {
        if (this.currentPage === 1) {
          await this.fetchPosts();
        } else {
          await this.pagingPosts();
        }
      } catch (error) {
        message.error(i18n.t('api_get_topics_error'));
      }
    }

    if (this.remainingTopicList.length < 20 && this.hasNextPage) {
      await this.processTopics();
    } else {
      runInAction(() => {
        this.topics = [...this.topics, ...this.remainingTopicList.slice(0, 20)];
        this.remainingTopicList = this.remainingTopicList.slice(20);
      });
    }

  };

  // > 2025/04/24 updated -> 要篩掉 news, forum, threads, ig 的重複 url  話題
  @action fetchPosts = async () => {
    try {
      if (mainStore.checkIsInQueue('topics')) {
        return;
      }
      mainStore.setLoading('topics');
      const { list, searchId } = await ResultService.getResultTopics(
        this.newsletterId,
        {
          date: {
            gte: this.startDate,
            lte: this.endDate
          },
          subjectId: this.selectedSubject
        }
      );

      // > 因為要過濾話題 size 改成先撈取 50 筆
      runInAction(() => {
        this.searchId = searchId;
        this.hasNextPage = list.length === 50;
        this.currentPage += 1;
      });

      this.filterPosts(list);
    } catch (error) {
      message.error(i18n.t('api_get_topics_error'));
      this.hasNextPage = false;
    } finally {
      mainStore.setLoadingComplete('topics');
    }
  };

  // > 2025/04/24 updated -> 要篩掉 news, forum, threads, ig 的重複 url  話題
  @action pagingPosts = async () => {
    try {
      if (mainStore.checkIsInQueue('topics')) {
        return;
      }
      mainStore.setLoading('topics');
      // > 因為要過濾話題 size 改成先撈取 50 筆
      const { list } = await ResultService.getResultTopicsByPage(this.newsletterId, { page: this.currentPage, searchId: this.searchId, size: 50 });

      // > 因為要過濾話題 size 改成先撈取 50 筆
      runInAction(() => {
        this.hasNextPage = list.length === 50;
        this.currentPage += 1;
      });

      this.filterPosts(list);

    } catch (error) {
      message.error(i18n.t('api_get_topics_error'));
      this.hasNextPage = false;
    } finally {
      mainStore.setLoadingComplete('topics');
    }
  };

  @action filterPosts = (posts) => {
    posts.forEach((post) => {
      if ([SOURCE_CATEGORY_TYPE.News, SOURCE_CATEGORY_TYPE.Forum, SOURCE_CATEGORY_TYPE.Threads, SOURCE_CATEGORY_TYPE.IG].includes(post.category)) {
        const url = post.url;
        if (this.urlSet.has(url)) {
          console.log('url exist', url, 'post is filtered', post);
          return;
        }
        this.urlSet.add(url);
        this.remainingTopicList.push(post);
      } else {
        this.remainingTopicList.push(post);
      }
    });
  };

  @action updateMainBrand = async () => {
    // > deprecated -> newsletter's main brand won't be null.
    // if (!this.mainBrand.id) {
    //   return;
    // }
    try {
      if (mainStore.checkIsInQueue('mainBrand')) {
        return;
      }
      mainStore.setLoading('mainBrand');
      const [brand] = await ProjectService.getLevelContentByIds(this.projectId, [this.mainBrand.id]);
      runInAction(() => {
        this.mainBrand.name = brand.name;
      });
    } catch (error) {
      message.error(i18n.t('api_level_one_not_found_error'));
    } finally {
      mainStore.setLoadingComplete('mainBrand');
    }
  };

  @action updateSubjectContent = async (item) => {
    const {
      id,
      hasEvaluation,
      searchKeywordLevel1Id,
      searchKeywordLevel2Id,
      searchKeywordLevel3Id,
      searchTagParentId,
      searchTagId,
      category
    } = item;

    try {
      const sourceText = !category[0]
        ? i18n.t('edm_drawer_topic_source_all')
        : `${SOURCE_CATEGORY_TC[category[0]]}`;

      let label = '';

      if (searchKeywordLevel1Id && searchKeywordLevel2Id) {
        const [brand, productLine] = await ProjectService.getLevelContentByIds(this.projectId, [searchKeywordLevel1Id, searchKeywordLevel2Id]);
        label = `${brand.name}/${productLine.name}-${sourceText}`;
      }

      if (searchKeywordLevel1Id && searchKeywordLevel3Id) {
        const [brand, product] = await ProjectService.getLevelContentByIds(this.projectId, [searchKeywordLevel1Id, searchKeywordLevel3Id]);
        label = `${brand.name}/${product.name}-${sourceText}`;
      }

      if (searchKeywordLevel1Id && !searchKeywordLevel2Id && !searchKeywordLevel3Id) {
        const [brand] = await ProjectService.getLevelContentByIds(this.projectId, [searchKeywordLevel1Id]);
        label = `${brand.name}-${sourceText}`;
      }

      if (searchTagParentId) {
        const [tagParent, tag] = await ProjectService.getTagContentByIds(this.projectId, [searchTagParentId, searchTagId]);
        if (searchKeywordLevel1Id) {
          const [brand] = await ProjectService.getLevelContentByIds(this.projectId, [searchKeywordLevel1Id]);
          label = `${brand.name}/${tagParent.name}/${tag.name}-${sourceText}`;
        } else {
          label = `${tagParent.name}/${tag.name}-${sourceText}`;
        }
      }

      return {
        value: id,
        label,
        hasEvaluation
      };

    } catch (error) {

      return {
        value: id,
        label: i18n.t('common_error_subject_title'),
        hasEvaluation
      };

    }
  };

  @action updateSearchAngle = async () => {
    try {
      if (mainStore.checkIsInQueue('searchAngle')) {
        return;
      }
      mainStore.setLoading('searchAngle');

      const { meta: { searchAngle } } = await ProjectService.getProjectDetailById(this.projectId);

      runInAction(() => {
        this.searchAngle = searchAngle;
      });

    } catch (error) {
      message.error(i18n.t('api_get_project_angle_error'));
    } finally {
      mainStore.setLoadingComplete('searchAngle');
    }
  };

  @action onMemoChange = (event) => {
    this.memo = event.target.value;
  };

  @action onMemoEditEnd = async () => {
    if (this.isMemoUpdating || this.memo === this.memoCache) {
      return;
    }
    this.isMemoUpdating = true;
    try {
      if (mainStore.checkIsInQueue('memoUpdating')) {
        return;
      }
      mainStore.setLoading('memoUpdating');
      await ResultService.updateResultDetail(this.newsletterId, this.resultId, this.memo);
      message.success(i18n.t('result_edit_page_memo_update_success'));
      runInAction(() => {
        this.memoCache = this.memo;
      });
    } catch (error) {
      message.error(i18n.t('api_post_memo_error'));
    } finally {
      runInAction(() => {
        this.isMemoUpdating = false;
      });
      mainStore.setLoadingComplete('memoUpdating');
    }

  };

  @action onSubjectSelect = async (value) => {
    this.selectedSubject = value;
    this.cleanUp();

    // ? 2025/04/10 新增 -> 切換話題的時候要把 status 設定成 null
    this.aiCommentStatus = null;
    this.aiCommentStatusDesc = '';

    await Promise.all([
      this.processTopics(),
      this.getAiCommentStatus()
    ]);
  };

  @action onEvalTypeChange = async (value, recordId, lv1Id) => {
    try {
      const target = this.topics.find((topic) => topic.id === recordId);
      if (!target) {
        return;
      }

      await ResultService.updateTopicAttribute(this.newsletterId, {
        subjectId: this.selectedSubject,
        mainId: recordId,
        lv1Id,
        attrType: TOPIC_ATTRIBUTE_TYPE.Rating,
        attrValue: value
      });

      runInAction(() => {
        target.evalType = value;
      });
    } catch (error) {
      message.error(i18n.t('api_put_update_eval_type_error'));
    }
  };

  @action onTopicAttrChange = async (value, recordId, lv1Id) => {
    try {
      const target = this.topics.find((topic) => topic.id === recordId);
      if (!target) {
        return;
      }

      await ResultService.updateTopicAttribute(this.newsletterId, {
        subjectId: this.selectedSubject,
        mainId: recordId,
        lv1Id,
        attrType: TOPIC_ATTRIBUTE_TYPE.Topic,
        attrValue: value
      });

      runInAction(() => {
        target.topicAttr = value;
      });
    } catch (error) {
      message.error(i18n.t('api_put_update_topic_attribute_error'));
    }
  };

  @action onReportAdviceChange = async (value, recordId, lv1Id) => {
    try {
      const target = this.topics.find((topic) => topic.id === recordId);
      if (!target) {
        return;
      }

      await ResultService.updateTopicAttribute(this.newsletterId, {
        subjectId: this.selectedSubject,
        mainId: recordId,
        lv1Id,
        attrType: TOPIC_ATTRIBUTE_TYPE.Suggestion,
        attrValue: value
      });

      runInAction(() => {
        target.reportAdvice = value;
      });
    } catch (error) {
      message.error(i18n.t('api_put_update_report_advice_error'));
    }
  };

  @action onDepartmentAttrChange = async (value, recordId, lv1Id) => {
    try {
      const target = this.topics.find((topic) => topic.id === recordId);
      if (!target) {
        return;
      }

      await ResultService.updateTopicAttribute(this.newsletterId, {
        subjectId: this.selectedSubject,
        mainId: recordId,
        lv1Id,
        attrType: TOPIC_ATTRIBUTE_TYPE.Department,
        attrValue: value
      });

      runInAction(() => {
        target.departmentAttr = value;
      });
    } catch (error) {
      message.error(i18n.t('api_put_update_department_attribute_error'));
    }

  };

  @action onShouldSendChange = async (boolean, recordId, lv1Id) => {
    try {
      const target = this.topics.find((topic) => topic.id === recordId);
      if (!target) {
        return;
      }

      await ResultService.updateTopicAttribute(this.newsletterId, {
        subjectId: this.selectedSubject,
        mainId: recordId,
        lv1Id,
        attrType: TOPIC_ATTRIBUTE_TYPE.ShouldSend,
        attrValue: boolean
      });

      runInAction(() => {
        target.shouldSend = boolean;
      });
    } catch (error) {
      message.error(i18n.t('api_put_update_should_send_error'));
    }
  };

  // > 2025/03/30 新增 ai comment 選項，重新產生 ai comment 的按鈕 function
  @action onRegenerateAiComment = async () => {
    if (mainStore.checkIsInQueue('regenerateAiComment')) {
      return;
    }
    mainStore.setLoading('regenerateAiComment');
    try {
      await ResultService.regenerateAiComment(this.newsletterId, this.selectedSubject, {
        gte: this.startDate,
        lte: this.endDate
      });
      message.success(i18n.t('result_edit_page_post_regenerate_ai_comment_success'));
      await this.getAiCommentStatus();
    } catch (error) {
      message.error(i18n.t('api_regenerate_ai_comment_error'));
    } finally {
      mainStore.setLoadingComplete('regenerateAiComment');
    }
  };

  // > 2025/03/30 新增 ai comment 選項，需要顯示目前 ai comment 的分析狀態
  // > 未開放評價的就不打 api 拉狀態了
  @action getAiCommentStatus = async () => {
    if (!this.aiComment || !this.selectedSubjectContent.hasEvaluation) {
      return;
    }
    if (mainStore.checkIsInQueue('getAiCommentStatus')) {
      return;
    }
    mainStore.setLoading('getAiCommentStatus');
    try {
      const {
        stage,
        progress,
        pending,
        beginAt,
        endAt
      } = await ResultService.getAiCommentStatus(this.newsletterId, this.selectedSubject, {
        gte: this.startDate,
        lte: this.endDate
      });
      runInAction(() => {
        // > 等候中 -> 需要顯示還要等候幾個
        if (stage === AI_COMMENT_STATUS.Waiting) {
          this.aiCommentStatus = stage;
          this.aiCommentStatusDesc = AI_COMMENT_STATUS_DESC[stage].replace('%%COUNT%%', pending);
        } else if (stage === AI_COMMENT_STATUS.Begin) {
          // > 進行中 -> 需要顯示 progress
          this.aiCommentStatus = stage;
          this.aiCommentStatusDesc = AI_COMMENT_STATUS_DESC[stage].replace('%%PROGRESS%%', `${Math.round(progress)}%`);
        } else if (stage === AI_COMMENT_STATUS.Done) {
          // > 完成 -> 需要顯示完成時間 (endAt - beginAt)
          // ? 2025/04/10 新增 -> 如果從其他狀況變成完成，則需要重新獲取評價結果 -> 切換話題的時候要把 status 設定成 null
          if (this.aiCommentStatus && this.aiCommentStatus !== AI_COMMENT_STATUS.Done) {
            this.cleanUp();
            this.processTopics();
          }
          // ? 2025/04/08 修改 -> 顯示起訖時間
          this.aiCommentStatus = stage;
          this.aiCommentStatusDesc = AI_COMMENT_STATUS_DESC[stage]
            .replace('%%START%%', `${dayjs(beginAt).format('YYYY/MM/DD HH:mm:ss')}\n`)
            .replace('%%END%%', dayjs(endAt).format('YYYY/MM/DD HH:mm:ss'));
        } else if (stage === AI_COMMENT_STATUS.Fail) {
          // > 失敗 -> 需要顯示失敗
          this.aiCommentStatus = stage;
          this.aiCommentStatusDesc = AI_COMMENT_STATUS_DESC[stage];
        } else {
          this.aiCommentStatus = stage;
          this.aiCommentStatusDesc = '';
        }
      });
    } catch (error) {
      message.error(i18n.t('api_get_ai_comment_status_error'));
    } finally {
      mainStore.setLoadingComplete('getAiCommentStatus');
      this.lastActionTime = performance.now();
      this.timerTick();
    }
  };

  @action timerTick = () => {
    const now = performance.now();
    const duration = dayjs.duration(now - this.lastActionTime);
    const seconds = duration.asSeconds();

    runInAction(() => {
      this.remainingTime = seconds;
      requestAnimationFrame(this.timerTick);
    });
  };

  // > 2025/04/25 updated -> 要篩掉 news, forum, threads, ig 的重複 url  話題
  @action cleanUp = () => {
    this.topics = [];
    this.remainingTopicList = [];
    this.searchId = null;
    this.currentPage = 1;
    this.hasNextPage = true;
    this.urlSet = new Set();
  };
}
